// Wires up click-to-reveal for spoilers on a post page.
//
// One spoiler the author created can render as several adjacent `.spoiler`
// spans: whenever the spoilered text also has inline formatting (bold, a link,
// inline code, a highlight, …) the editor wraps each formatted piece in its own
// `.spoiler` span. We treat such a group of adjacent spans as a single spoiler,
// so clicking any one of them reveals the whole group at once.
//
// Two spans belong to the same group when there is no text between them and
// both live in the same rendered block container. This keeps adjacent spoilers
// in different paragraphs/list items/headings/quotes (and other block nodes)
// separate even when no text node exists between those blocks — so e.g. a list
// of hints reveals one item at a time.
//
// Revealing is one-way: once revealed it stays revealed, so a click on a link
// inside the spoiler follows the link instead of hiding the text again.
// `display` values that don't establish a block container; it keeps walking up
// to find the spoiler's block. jsdom reports inline elements as '' rather than
// 'inline', so both must count as transparent here.
const INLINE_DISPLAYS = new Set(['', 'inline', 'contents']);
export function setupSpoilerReveal(root) {
    const spoilers = Array.from(root.querySelectorAll('.spoiler'));
    const blockContainer = (spoiler) => {
        let node = spoiler.parentElement;
        while (node && node !== root) {
            if (!INLINE_DISPLAYS.has(window.getComputedStyle(node).display)) {
                return node;
            }
            node = node.parentElement;
        }
        return root;
    };
    const contiguous = (a, b) => {
        if (blockContainer(a) !== blockContainer(b)) {
            return false;
        }
        const range = document.createRange();
        range.setStartAfter(a);
        range.setEndBefore(b);
        // Links inject a screen-reader-only "(opens in a new window)" note; ignore it
        // so a spoiler that wraps a link still counts as one contiguous run.
        const between = range.cloneContents();
        between.querySelectorAll('.sr_only').forEach(node => node.remove());
        return between.textContent === '';
    };
    // Spans come back in document order, so each contiguous run is one group.
    // Partition once up front; clicking any span then reveals its whole group.
    const groups = [];
    spoilers.forEach((spoiler, index) => {
        const previous = spoilers[index - 1];
        if (previous && contiguous(previous, spoiler)) {
            groups[groups.length - 1].push(spoiler);
        }
        else {
            groups.push([spoiler]);
        }
    });
    groups.forEach(group => {
        group.forEach(spoiler => {
            spoiler.addEventListener('click', event => {
                if (spoiler.classList.contains('is_revealed')) {
                    return;
                }
                event.preventDefault();
                group.forEach(member => member.classList.add('is_revealed'));
            });
        });
    });
}
